import { Flame, Home, Snowflake, Thermometer } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AddLeadDialog } from '@/modules/marketing/components/leads/AddLeadDialog'
import { DataTable } from '@/shared/components/data-table'
import { ExpandableText } from '@/shared/components/ExpandableText'
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import { Badge } from '@/shared/components/ui/badge'
import { ViewProductsModal } from '@/shared/components/ViewProductsModal'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'

import { getQuotationById, getQuotationByLeadId } from '../../api/quotation.api'
import { confirmLeadFollowUp } from '../../api/sales.api'
import { useSalesLeads } from '../../hooks/useSalesLeads'
import { useCommentsStore } from '../../stores/comments.store'
import QuoteDetailsModal from '../quotations/QuoteDetailsModal'

import LeadActionButtons from './LeadActionButtons'
import LeadCommentsModal from './LeadCommentsModal'
import LeadStatusDropdown from './LeadStatusDropdown'

function LinkedInIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M6.94 6.5A1.94 1.94 0 1 1 5 4.56 1.94 1.94 0 0 1 6.94 6.5ZM7 8.75H5v10.5h2V8.75Zm4.25 0H9.25v10.5h2v-5.5c0-1.45.83-2.25 2.05-2.25 1.12 0 1.7.75 1.7 2.25v5.5H17v-5.9c0-3.05-1.63-4.45-3.8-4.45-1.75 0-2.52.97-2.95 1.65V8.75Z" />
    </svg>
  )
}

function FacebookIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M14.5 8.25h1.75V5.5H14.4c-2.2 0-3.65 1.35-3.65 3.7v1.55H8.75v2.75h2v7.75h3.1v-7.75h2.2l.45-2.75h-2.65V9.4c0-.7.3-1.15 1.15-1.15Z" />
    </svg>
  )
}

const SOURCE_STYLES = {
  linkedin: {
    label: 'LinkedIn',
    className: 'border-sky-200 bg-sky-50 text-sky-700',
    icon: LinkedInIcon,
  },
  facebook: {
    label: 'Facebook',
    className: 'border-blue-200 bg-blue-50 text-blue-700',
    icon: FacebookIcon,
  },
  offline: {
    label: 'Offline',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    icon: Home,
  },
}

const PAGE_SIZE = 5

function SourceBadge({ source }) {
  const config = SOURCE_STYLES[source] || SOURCE_STYLES.offline
  const Icon = config.icon

  return (
    <Badge
      className={cn(
        'gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold',
        config.className,
      )}
    >
      <Icon className="size-3" />
      {config.label}
    </Badge>
  )
}

export default function SalesLeadsTable({
  source,
  status = '',
  leadWarmth = '',
  period = 'all',
  customRange = { startDate: '', endDate: '' },
  onEmail,
}) {
  /*
   * searchInput:
   * What the user is currently typing.
   *
   * search:
   * The value currently being sent to the API.
   */
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const [page, setPage] = useState(1)

  const navigate = useNavigate()

  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState(null)
  const [isEditLeadOpen, setIsEditLeadOpen] = useState(false)
  const [isProductsOpen, setIsProductsOpen] = useState(false)
  const [confirmedFollowUps, setConfirmedFollowUps] = useState({})
  const [selectedQuote, setSelectedQuote] = useState(null)
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false)

  const { leads, meta, isLoading, fetchLeads, updateLeadStatus } =
    useSalesLeads()

  const notifications = useCommentsStore((state) => state.notifications)

  const getUnreadCountForLead = (leadId) =>
    notifications.filter(
      (notification) => notification.leadId === leadId && !notification.isRead,
    ).length

  /*
   * Reset page when filters change
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1)
  }, [
    source,
    status,
    leadWarmth,
    period,
    customRange?.startDate,
    customRange?.endDate,
  ])

  /*
   * Update API search after the user pauses briefly.
   *
   * IMPORTANT:
   * searchInput remains independent, so the input value
   * never depends on the API response/loading state.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchInput])

  /*
   * Fetch leads using the current API search value.
   */
  useEffect(() => {
    fetchLeads({
      page,
      limit: PAGE_SIZE,
      search,
      source: source === 'all' ? undefined : source,
      status: status === 'all' ? undefined : status,
      leadWarmth: leadWarmth === 'all' ? undefined : leadWarmth,
      period: period === 'all' ? undefined : period,
      startDate:
        period === 'custom' ? customRange?.startDate || undefined : undefined,
      endDate:
        period === 'custom' ? customRange?.endDate || undefined : undefined,
    })
  }, [
    fetchLeads,
    page,
    search,
    source,
    status,
    leadWarmth,
    period,
    customRange?.startDate,
    customRange?.endDate,
  ])

  const handleSearchChange = (value) => {
    // Update the input immediately.
    // No API call here.
    setSearchInput(value)
  }

  const handleConfirmFollowUp = async (lead) => {
    try {
      await confirmLeadFollowUp(lead._id)

      setConfirmedFollowUps((prev) => ({
        ...prev,
        [lead._id]: true,
      }))

      setTimeout(() => {
        setConfirmedFollowUps((prev) => {
          const updated = { ...prev }
          delete updated[lead._id]
          return updated
        })

        fetchLeads({
          page,
          limit: PAGE_SIZE,
          search,
          source: source === 'all' ? undefined : source,
        })
      }, 1000)
    } catch (error) {
      console.error('Failed to confirm follow-up:', error)
    }
  }

  const handleViewQuote = async (row) => {
    try {
      if (row.quotation?._id) {
        const res = await getQuotationById(row.quotation._id)
        setSelectedQuote(res?.data || res || row.quotation)
      } else {
        const res = await getQuotationByLeadId(row._id)
        setSelectedQuote(res?.data || res)
      }
      setIsQuoteModalOpen(true)
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load quotation details',
      )
    }
  }

  const columns = [
    {
      id: 'lead',
      header: 'Company',
      cell: (row) => (
        <div className="flex min-w-[180px] items-center gap-3 sm:min-w-[200px]">
          <Avatar className="size-9">
            <AvatarFallback className="bg-muted text-xs font-bold">
              {(row.company || row.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0">
            {/* Company Name + Warmth */}
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-bold text-foreground">
                {row.company || '-'}
              </p>

              {row.leadWarmth === 'hot' && (
                <Flame className="size-3.5 text-orange-500" />
              )}

              {row.leadWarmth === 'cold' && (
                <Snowflake className="size-3.5 text-sky-500" />
              )}

              {row.leadWarmth === 'warm' && (
                <Thermometer className="size-3.5 text-amber-500" />
              )}
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'createdAt',
      header: 'Date',
      cell: (row) => {
        if (!row.createdAt)
          return <span className="text-muted-foreground">-</span>
        const d = new Date(row.createdAt)
        return (
          <span className="whitespace-nowrap text-xs font-medium text-slate-600">
            {d.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        )
      },
    },

    {
      id: 'source',
      header: 'Source',
      cell: (row) => <SourceBadge source={row.source} />,
    },

    {
      id: 'product',
      header: 'Product',
      cell: (row) => {
        const products = row.products || []
        const productsWithDesc = products.filter(
          (p) =>
            typeof p.description === 'string' &&
            p.description.trim().length > 0,
        )
        const productsWithoutDesc = products.filter(
          (p) =>
            !p.description ||
            typeof p.description !== 'string' ||
            p.description.trim().length === 0,
        )
        const isPartialDesc =
          products.length > 1 &&
          productsWithDesc.length > 0 &&
          productsWithoutDesc.length > 0

        const firstProduct = isPartialDesc
          ? productsWithDesc[0]?.product || '-'
          : products[0]?.product || '-'
        const restCount = products.length - 1

        return (
          <div className="flex items-center gap-1.5 max-w-[200px]">
            <ExpandableText
              text={firstProduct}
              maxLength={20}
              expandable={false}
            />

            {restCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedLead(row)
                  setIsProductsOpen(true)
                }}
                className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600 cursor-pointer"
              >
                +{restCount} more
              </button>
            )}
          </div>
        )
      },
    },

    {
      id: 'description',
      header: 'Description',
      cell: (row) => {
        const products = row.products || []
        const productsWithDesc = products.filter(
          (p) =>
            typeof p.description === 'string' &&
            p.description.trim().length > 0,
        )
        const productsWithoutDesc = products.filter(
          (p) =>
            !p.description ||
            typeof p.description !== 'string' ||
            p.description.trim().length === 0,
        )
        const isPartialDesc =
          products.length > 1 &&
          productsWithDesc.length > 0 &&
          productsWithoutDesc.length > 0

        const sourceList = isPartialDesc ? productsWithDesc : products
        const descriptions = sourceList
          .map((p) =>
            typeof p.description === 'string' ? p.description.trim() : '',
          )
          .filter(Boolean)

        if (!descriptions.length && row.description?.trim()) {
          descriptions.push(row.description.trim())
        }
        if (!descriptions.length)
          return <span className="text-muted-foreground">-</span>

        const firstDesc = descriptions[0]
        const restDescCount = descriptions.length - 1

        return (
          <div className="flex items-center gap-1.5 max-w-[200px]">
            <ExpandableText
              text={firstDesc}
              maxLength={20}
              expandable={false}
            />
            {restDescCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedLead(row)
                  setIsProductsOpen(true)
                }}
                className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600 cursor-pointer"
              >
                +{restDescCount} more
              </button>
            )}
          </div>
        )
      },
    },

    {
      id: 'quantity',
      header: 'Quantity',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (row) => {
        const products = row.products || []

        if (!products.length) {
          return <span className="text-muted-foreground">-</span>
        }

        const totalQty = products.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0),
          0,
        )

        return (
          <span className="text-sm font-semibold text-foreground">
            {totalQty}
          </span>
        )
      },
    },

    {
      id: 'value',
      header: 'Value',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (row) => {
        const products = Array.isArray(row.products) ? row.products : []

        const totalFromProducts = products.reduce((sum, item) => {
          const qty = Number(item?.quantity ?? item?.qty) || 1
          const val =
            Number(
              item?.value ?? item?.unitPrice ?? item?.price ?? item?.rate,
            ) || 0
          return sum + qty * val
        }, 0)

        const totalValue =
          totalFromProducts > 0
            ? totalFromProducts
            : Number(
                row?.value ??
                  row?.dealValue ??
                  row?.totalValue ??
                  row?.amount ??
                  totalFromProducts,
              ) || 0

        if (!products.length && totalValue === 0) {
          return <span className="text-muted-foreground">-</span>
        }

        return (
          <span className="text-sm font-semibold text-foreground">
            ₹{totalValue.toLocaleString('en-IN')}
          </span>
        )
      },
    },

    {
      id: 'salesPerson',
      header: 'Sales Person',
      cell: (row) => (
        <span className="text-sm font-medium text-foreground">
          {row.salesPerson?.name || row.createdBy?.name || '-'}
        </span>
      ),
    },

    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <LeadStatusDropdown
          value={row.status || 'open'}
          onChange={async (value) => {
            try {
              await updateLeadStatus(row._id, value)
            } catch (error) {
              console.error(error)
            }
          }}
        />
      ),
    },

    {
      id: 'quotationStatus',
      header: 'Quotation Status',
      cell: (row) => {
        const quote = row.quotation
        if (quote?._id) {
          return (
            <div className="flex items-center gap-1.5">
              <Badge
                className="gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 cursor-pointer hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                title={
                  quote.quoteNo
                    ? `Click to view Quote #${quote.quoteNo}`
                    : 'Click to view Quotation'
                }
                onClick={() => handleViewQuote(row)}
              >
                <span className="size-1.5 rounded-full bg-emerald-500" />
                {quote.isSent || quote.status === 'sent' ? 'Sent' : 'Created'}
                {quote.quoteNo ? ` (${quote.quoteNo})` : ''}
              </Badge>
              <button
                type="button"
                onClick={() =>
                  navigate(`/sales/quotes/edit/${quote._id}`, {
                    state: { from: 'leads', lead: row },
                  })
                }
                title="Edit Quotation Products"
                className="rounded-full p-1 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition"
              ></button>
            </div>
          )
        }
        return (
          <Badge className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-500">
            Not Sent
          </Badge>
        )
      },
    },

    {
      id: 'actions',
      header: 'Actions',
      headerClassName: 'w-[280px] min-w-[280px] max-w-[280px] text-left',
      className: 'w-[280px] min-w-[280px] max-w-[280px]',

      cell: (row) => (
        <div className="flex w-[280px] min-w-[280px] max-w-[280px] justify-start">
          <LeadActionButtons
            unreadCount={getUnreadCountForLead(row._id)}
            onCall={() => {
              if (row.phone) {
                window.location.href = `tel:${row.phone}`
              } else {
                toast.info('No phone number available for this lead')
              }
            }}
            onEmail={() => {
              if (row.email) {
                onEmail(row)
              } else {
                toast.info('No email address available for this lead')
              }
            }}
            onMessage={() => {
              setSelectedLead(row)
              setIsCommentsOpen(true)
            }}
            hasQuotation={Boolean(row.quotation?._id)}
            onQuotation={() => {
              navigate(`/sales/leads/create-quotation/${row._id}`, {
                state: {
                  from: 'leads',
                  lead: row,
                },
              })
            }}
            onEdit={() => {
              setSelectedLeadForEdit(row)
              setIsEditLeadOpen(true)
            }}
            onView={() => {
              setSelectedLead(row)
              setIsProductsOpen(true)
            }}
            onConfirmFollowUp={() => handleConfirmFollowUp(row)}
            canConfirmFollowUp={
              (row.followUpSent === true && row.followUpConfirmed !== true) ||
              confirmedFollowUps[row._id] === true
            }
            followUpConfirmed={
              row.followUpConfirmed === true ||
              confirmedFollowUps[row._id] === true
            }
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="w-full min-w-0 overflow-x-auto">
        <DataTable
          columns={columns}
          data={leads}
          tableClassName="min-w-[1100px] sm:min-w-[1350px]"
          isLoading={isLoading}
          search={{
            value: searchInput,
            onChange: handleSearchChange,
            placeholder: 'Search leads by name, company, phone...',
          }}
          filterSummary={`${meta?.total || 0} Leads`}
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            total: meta?.total || 0,
            onPageChange: setPage,
          }}
          emptyMessage="No assigned leads found."
        />
      </div>

      <LeadCommentsModal
        open={isCommentsOpen}
        onOpenChange={setIsCommentsOpen}
        lead={selectedLead}
        conversationWith="SUPER ADMIN"
      />

      <ViewProductsModal
        open={isProductsOpen}
        onOpenChange={setIsProductsOpen}
        lead={selectedLead}
      />

      <AddLeadDialog
        open={isEditLeadOpen}
        onOpenChange={(open) => {
          setIsEditLeadOpen(open)
          if (!open) setSelectedLeadForEdit(null)
        }}
        lead={selectedLeadForEdit}
        module="sales"
        onSuccess={() => {
          fetchLeads({
            page,
            limit: PAGE_SIZE,
            search,
            source: source === 'all' ? undefined : source,
          })
        }}
      />

      <QuoteDetailsModal
        open={isQuoteModalOpen}
        onOpenChange={setIsQuoteModalOpen}
        quote={selectedQuote}
      />
    </>
  )
}
